/**
 * pipelineStages.js — single source of truth for the revised Lead Pipeline
 * (v2.0, May 2026 — see /app/pipeline_stages_revised.md).
 *
 * Everything that needs to know "what stages exist" or "what sub-statuses
 * are valid for stage X" reads from THIS file. Do not duplicate these
 * arrays anywhere else.
 *
 * Consumers:
 *   - Leads.jsx (Kanban COLUMNS, list filter dropdowns)
 *   - LeadFocus.jsx (hero stage + sub-status pickers)
 *   - LeadFocusVitals.jsx (vitals stage + sub-status rows)
 *   - LeadSegments / Audiences (filter dropdowns)
 *   - leadStage.js (STAGE_TO_COLUMN normalizer)
 */

// Authoritative stage list, in pipeline order.
// `id` is the slug stored on `lead.stage`. `label` is shown in the UI.
// `colorFamily` drives the Kanban column header tint.
export const STAGES = Object.freeze([
  { id: "new_lead",      label: "New Leads",            colorFamily: "blue"   },
  { id: "contacted",     label: "Contacted",            colorFamily: "green"  },
  { id: "demo",          label: "Demo",                 colorFamily: "purple" },
  { id: "post_demo",     label: "Post-Demo Follow-Up",  colorFamily: "blue"   }, // NEW (v2)
  { id: "proposal",      label: "Proposal",             colorFamily: "amber"  },
  { id: "decision",      label: "Decision",             colorFamily: "teal"   },
  { id: "closed_won",    label: "Closed Won",           colorFamily: "green"  },
  { id: "implementation",label: "Implementation",       colorFamily: "indigo" },
  { id: "closed_lost",   label: "Closed Lost",          colorFamily: "red"    },
  { id: "inactive",      label: "Inactive",             colorFamily: "gray"   },
]);

// Hex per colorFamily — kept here so consumers can pull a column tint
// without re-implementing the palette.
export const STAGE_COLORS = Object.freeze({
  blue:   "#0071e3",
  green:  "#1a7a52",
  purple: "#5448a8",
  amber:  "#b06a14",
  teal:   "#1a7070",
  indigo: "#1a5c8a",
  red:    "#aa2a2a",
  gray:   "#71706b",
});

// ── Iter 195.18 — Stage → default close-probability ─────────────
// Classic sales-pipeline weighting (Salesforce convention). Applied
// automatically on lead creation and whenever `stage` changes, so
// the /leads PROB column and weighted-pipeline dashboards produce a
// meaningful number without every rep having to manually enter it.
// Rep-entered manual `probability` values are preserved (we only
// stamp when the field is 0 / missing OR the caller explicitly asks
// via `?refresh_probability=true`).
// Numbers are baseline defaults — admins can tune later via a
// per-tenant `stage_probability_config` doc without a code change.
export const STAGE_PROBABILITY = Object.freeze({
  new_lead:       10,
  contacted:      25,
  demo:           40,
  post_demo:      50,
  proposal:       65,
  decision:       80,
  closed_won:    100,
  implementation: 100,
  closed_lost:     0,
  inactive:        0,
});

/**
 * Resolve a lead's probability. Rep-entered values (> 0) win; if the
 * stored value is 0/missing we fall back to the stage default.
 */
export function resolveProbability(lead) {
  const stored = Number(lead?.probability);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const stageDefault = STAGE_PROBABILITY[lead?.stage];
  return Number.isFinite(stageDefault) ? stageDefault : 0;
}

// Sub-statuses per stage. Keys MUST match STAGES[].id.
// Each entry: { value: <stored on lead.sub_status>, label: <shown>, isLossReason?: bool }
// `value` is what gets persisted; `label` may include human-readable
// characters (em dashes, slashes, etc.). Order is the display order.
export const SUB_STATUSES = Object.freeze({
  new_lead: [],

  contacted: [
    // Tier 1 — Gatekeeper contacts
    { value: "Contacted — Front Desk / Receptionist", label: "Contacted — Front Desk / Receptionist", tier: "gatekeeper" },
    { value: "Contacted — Medical Assistant (MA)",    label: "Contacted — Medical Assistant (MA)",    tier: "gatekeeper" },
    { value: "Contacted — Patient Coordinator",       label: "Contacted — Patient Coordinator",       tier: "gatekeeper" },
    { value: "Contacted — Referral Coordinator",      label: "Contacted — Referral Coordinator",      tier: "gatekeeper" },
    // Tier 2 — Administrative
    { value: "Contacted — Office Manager",            label: "Contacted — Office Manager",            tier: "admin" },
    { value: "Contacted — Practice Manager",          label: "Contacted — Practice Manager",          tier: "admin" },
    { value: "Contacted — Practice Administrator",    label: "Contacted — Practice Administrator",    tier: "admin" },
    { value: "Contacted — Revenue Cycle Manager",     label: "Contacted — Revenue Cycle Manager",     tier: "admin" },
    { value: "Contacted — Billing Manager / Biller",  label: "Contacted — Billing Manager / Biller",  tier: "admin" },
    { value: "Contacted — Compliance Officer",        label: "Contacted — Compliance Officer",        tier: "admin" },
    { value: "Contacted — HR Manager",                label: "Contacted — HR Manager",                tier: "admin" },
    // Tier 3 — Physician / Clinical
    { value: "Contacted — Physician / Doctor (MD/DO)",      label: "Contacted — Physician / Doctor (MD/DO)",      tier: "clinical" },
    { value: "Contacted — Physician Owner / Partner",       label: "Contacted — Physician Owner / Partner",       tier: "clinical" },
    { value: "Contacted — Nurse Practitioner (NP)",         label: "Contacted — Nurse Practitioner (NP)",         tier: "clinical" },
    { value: "Contacted — Physician Assistant (PA)",        label: "Contacted — Physician Assistant (PA)",        tier: "clinical" },
    { value: "Contacted — Medical Director",                label: "Contacted — Medical Director",                tier: "clinical" },
    { value: "Contacted — Nurse Manager / Director of Nursing", label: "Contacted — Nurse Manager / Director of Nursing", tier: "clinical" },
    // Tier 4 — Executive level
    { value: "Contacted — CEO",                       label: "Contacted — CEO",                       tier: "executive" },
    { value: "Contacted — CFO",                       label: "Contacted — CFO",                       tier: "executive" },
    { value: "Contacted — COO",                       label: "Contacted — COO",                       tier: "executive" },
    { value: "Contacted — CMO / Chief Medical Officer", label: "Contacted — CMO / Chief Medical Officer", tier: "executive" },
    { value: "Contacted — VP of Operations",          label: "Contacted — VP of Operations",          tier: "executive" },
    { value: "Contacted — Director of Operations",    label: "Contacted — Director of Operations",    tier: "executive" },
    { value: "Contacted — Executive Director",        label: "Contacted — Executive Director",        tier: "executive" },
    // Universal — No live contact
    { value: "Left Voicemail",                        label: "Left Voicemail",                        tier: "no_contact" },
    { value: "Needs Follow-Up",                       label: "Needs Follow-Up",                       tier: "no_contact" },
    // Iter 210.8 (Feb 2026 · AriaOne "unify after-call-work" disposition
    // simplification). New "no live contact" sub-statuses added at the
    // request of the field team. Tier stays "no_contact" so DM Contact
    // Rate KPIs remain accurate (none of these imply a decision-maker
    // conversation).
    { value: "No Live Contact",                       label: "No Live Contact",                       tier: "no_contact" },
    { value: "No Answer",                             label: "No Answer",                             tier: "no_contact" },
    { value: "Wrong Number",                          label: "Wrong Number",                          tier: "no_contact" },
    { value: "Number Disconnected / Fast Busy",       label: "Number Disconnected / Fast Busy",       tier: "no_contact" },
    { value: "Left On Hold",                          label: "Left On Hold",                          tier: "no_contact" },
    { value: "Not Interested",                        label: "Not Interested",                        tier: "no_contact" },
    { value: "Do Not Call List Request",              label: "Do Not Call List Request",              tier: "no_contact" },
  ],

  demo: [
    // Scheduling states
    { value: "Demo Scheduled",   label: "Demo Scheduled",   tier: "demo_status" },
    { value: "Demo Rescheduled", label: "Demo Rescheduled", tier: "demo_status" },
    { value: "Demo No-Show",     label: "Demo No-Show",     tier: "demo_status" },
    // Demo Completed → outcome sub-sub-statuses (mirrors the Log Demo
    // Outcome enum). Picking one implies the demo happened.
    { value: "Highly Interested",  label: "Highly Interested",  tier: "demo_completed" },
    { value: "Requested Proposal", label: "Requested Proposal", tier: "demo_completed" },
    { value: "Needs Follow-up",    label: "Needs Follow-up",    tier: "demo_completed" },
    { value: "Need More Info",     label: "Need More Info",     tier: "demo_completed" },
    { value: "Not a Fit",          label: "Not a Fit",          tier: "demo_completed" },
  ],

  post_demo: [
    { value: "Another Meeting / Demo Requested", label: "Another Meeting / Demo Requested" },
    { value: "Sent Follow-Up Materials",         label: "Sent Follow-Up Materials"         },
    { value: "Awaiting Internal Decision",       label: "Awaiting Internal Decision"       },
    { value: "Proposal Requested",               label: "Proposal Requested"               },
    { value: "Ghosting / No Response",           label: "Ghosting / No Response"           },
  ],

  proposal: [
    { value: "Quote Sent",         label: "Quote Sent"         },
    { value: "Contract Sent",      label: "Contract Sent"      },
    { value: "Under Review",       label: "Under Review"       },
    { value: "Revision Requested", label: "Revision Requested" },
    { value: "Verbal Commitment",  label: "Verbal Commitment"  },
  ],

  decision: [
    { value: "Negotiation",              label: "Negotiation"              },
    { value: "Decision Pending",         label: "Decision Pending"         },
    { value: "On Hold",                  label: "On Hold"                  },
    { value: "Escalated to Leadership",  label: "Escalated to Leadership"  }, // NEW (v2)
  ],

  closed_won: [],

  implementation: [
    { value: "Implementation — Setup",         label: "Implementation — Setup"         },
    { value: "Implementation — Configuration", label: "Implementation — Configuration" },
    { value: "Implementation — Training",      label: "Implementation — Training"      },
    { value: "Implementation — Go Live",       label: "Implementation — Go Live"       },
    { value: "Implementation — Support",       label: "Implementation — Support"       },
    { value: "Implementation Lost",            label: "Implementation Lost"            },
  ],

  // Loss-reason sub-statuses — REQUIRED before a lead can be moved here.
  closed_lost: [
    { value: "Chose Competitor",  label: "Chose Competitor",  isLossReason: true },
    { value: "Price / Budget",    label: "Price / Budget",    isLossReason: true },
    { value: "No Decision Made",  label: "No Decision Made",  isLossReason: true },
    { value: "Not a Fit",         label: "Not a Fit",         isLossReason: true },
    { value: "Bad Timing",        label: "Bad Timing",        isLossReason: true },
    { value: "Unresponsive",      label: "Unresponsive",      isLossReason: true },
    { value: "Invalid Lead",      label: "Invalid Lead",      isLossReason: true },
  ],

  inactive: [
    { value: "Re-Engagement — 10–30 Days", label: "Re-Engagement — 10–30 Days" },
    { value: "Re-Engagement — 30+ Days",   label: "Re-Engagement — 30+ Days"   },
    { value: "Long-Term Hold",             label: "Long-Term Hold"             },
    { value: "Do Not Contact",             label: "Do Not Contact"             },
  ],
});

// Iter 246 — the Demo sub-statuses that mean the demo actually happened
// (the "Demo Completed" tier + the legacy "Demo Completed" value).
// Reporting rollups treat any of these as a completed demo.
export const DEMO_COMPLETED_SUB_STATUSES = Object.freeze([
  "Demo Completed",
  ...(SUB_STATUSES.demo || []).filter((s) => s.tier === "demo_completed").map((s) => s.value),
]);

/**
 * Returns the list of valid sub-status options for a given stage.
 * Falls back to [] if the stage is unknown.
 */
export function getSubStatuses(stageId) {
  return SUB_STATUSES[stageId] || [];
}

/**
 * True iff `stageId` is the Closed Lost stage. Used to drive the
 * required-loss-reason modal on stage change.
 */
export function isClosedLost(stageId) {
  return stageId === "closed_lost";
}

/**
 * Helper for the kanban — returns the COLUMNS array shape consumed by
 * Leads.jsx (id + label + color + subs label list).
 */
export function getKanbanColumns() {
  return STAGES.map(s => ({
    id: s.id,
    label: s.label,
    color: STAGE_COLORS[s.colorFamily] || STAGE_COLORS.gray,
    subs: (SUB_STATUSES[s.id] || []).map(ss => ss.label),
  }));
}

/**
 * Lookup helper: given any legacy stage value, return the v2 stage id.
 * Mirrors backfill_pipeline_v2.py's MIGRATION_MAP — kept in sync so
 * frontend can reshape legacy leads at render time if needed.
 */
export const LEGACY_STAGE_MAP = Object.freeze({
  // slug-style legacy
  "new":              "new_lead",
  "follow_up":        "contacted",
  "demo_scheduled":   "demo",
  "demo_confirmed":   "demo",
  "demo_completed":   "demo",
  "demo_rescheduled": "demo",
  "quote_sent":       "proposal",
  "negotiation":      "decision",
  "on_hold":          "decision",
  "won":              "closed_won",
  "lost":             "closed_lost",
  "contract":         "closed_won",
  "invalid":          "closed_lost",
  "implementation_lost": "implementation",
  "stalled":          "inactive",

  // human-label legacy (lowercased + dash-normalized for lookup)
  "new lead - immediate follow-up": "new_lead",
  "immediate follow-up":            "new_lead",
  "proposal sent":                  "proposal",
  "decision pending":               "decision",
  "re-engagement (30+ days)":       "inactive",
  "re-engagement (10-30 days)":     "inactive",
});

/**
 * Lookup helper: given a legacy sub-status, return the v2 sub-status
 * value (or null if no clean mapping — caller should default to a sane
 * v2 sub-status like "Needs Follow-Up").
 */
export const LEGACY_SUB_STATUS_MAP = Object.freeze({
  // Contacted (old v2-A 6-item list → v2-B 24-item list, May 2026)
  "contacted — front office":   "Contacted — Front Desk / Receptionist",
  "contacted — office manager": "Contacted — Office Manager", // unchanged
  "contacted — doctor":         "Contacted — Physician / Doctor (MD/DO)",
  "contacted — biller":         "Contacted — Billing Manager / Biller",
  // Contacted (old → new)
  "awaiting intro call":   "Needs Follow-Up",
  "awaiting intro":        "Needs Follow-Up",
  "email sent":            "Needs Follow-Up",
  "reply received":        "Needs Follow-Up",
  "voicemail left":        "Left Voicemail",
  "contacted":             "Needs Follow-Up",
  "connected":             "Needs Follow-Up",
  "follow-up":             "Needs Follow-Up",
  "re-engagement 10-30 days": "Needs Follow-Up",
  "re-engagement 30+ days":   "Needs Follow-Up",

  // Demo (old → new)
  "demo scheduled":   "Demo Scheduled",
  "demo rescheduled": "Demo Rescheduled",
  "demo completed":   "Demo Completed",
  "follow-up pending": "Demo Completed",

  // Proposal (old → new)
  "create quote":        "Quote Sent",
  "proposal sent":       "Quote Sent",
  "under review":        "Under Review",
  "revision requested":  "Revision Requested",

  // Decision (old → new)
  "pricing under review": "Negotiation",
  "legal review":         "Negotiation",
  "stuck":                "On Hold",

  // Closed Won (legacy contract column)
  "contract out for signature": "Contract Sent", // becomes Proposal/Contract Sent
  "redlines received":          "Revision Requested",
  "signed":                     null, // → stays Closed Won, no sub-status

  // Implementation
  "kickoff scheduled":   "Implementation — Setup",
  "in progress":         "Implementation — Configuration",
  "paused":              "Implementation — Setup",
  "setup":               "Implementation — Setup",
  "configuration":       "Implementation — Configuration",
  "training":            "Implementation — Training",
  "go live":             "Implementation — Go Live",
  "support":             "Implementation — Support",

  // Inactive
  "on hold — long term": "Long-Term Hold",
  "on hold - long term": "Long-Term Hold",
  "stalled":             "Re-Engagement — 30+ Days",
});
